/**
 * Passkey/WebAuthn utility functions for browser-side authentication
 */

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials !== "undefined"
  );
}

/**
 * Convert ArrayBuffer to base64url string
 */
export function bufferToBase64URL(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Convert base64url string to Uint8Array
 */
export function base64urlToBuffer(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Get the Relying Party ID for WebAuthn
 * Uses the current hostname, but falls back to "localhost" for IP addresses
 * (IP addresses are not valid as WebAuthn rp.id)
 */
export function getRpId(): string {
  if (typeof window === "undefined") return "localhost";
  const hostname = window.location.hostname;
  // IP addresses are not valid for WebAuthn rp.id - must be a domain
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname) || /^[0-9a-fA-F:]+$/.test(hostname)) {
    return "localhost";
  }
  return hostname;
}

/**
 * Interface for passkey registration result
 */
export interface PasskeyRegistrationData {
  credentialId: string;
  publicKeyX: string;
  publicKeyY: string;
}

/**
 * Interface for passkey login data
 */
export interface PasskeyLoginData {
  credentialId: string;
  authenticatorData: string;
  clientDataJSON: string;
  signature: string;
  challenge: string;
}

/**
 * Register a new passkey (navigator.credentials.create)
 * Returns the credential data to send to the server
 */
export async function registerPasskey(name?: string): Promise<PasskeyRegistrationData> {
  if (!isWebAuthnSupported()) {
    throw new Error("WebAuthn is not supported in this browser");
  }

  const rpId = getRpId();

  // Generate random challenge and user ID
  const challengeBytes = new Uint8Array(32);
  crypto.getRandomValues(challengeBytes);

  const userIdBytes = new Uint8Array(32);
  crypto.getRandomValues(userIdBytes);

  // Create the credential
  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: challengeBytes as any,
      rp: {
        name: "OTC",
        id: rpId,
      },
      user: {
        id: userIdBytes as any,
        name: name || "OTC User",
        displayName: name || "OTC User",
      },
      pubKeyCredParams: [
        { alg: -7, type: "public-key" as const }, // ES256 / P-256
      ],
      authenticatorSelection: {
        userVerification: "required" as const,
        residentKey: "required" as const,
        authenticatorAttachment: "platform" as const,
      },
      timeout: 120000,
      attestation: "direct",
    },
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Passkey registration was cancelled");
  }

  const response = credential.response as AuthenticatorAttestationResponse;

  // Extract public key using getPublicKey()
  const publicKeyBuffer = (response as any).getPublicKey();
  if (!publicKeyBuffer) {
    throw new Error("Failed to get public key from authenticator");
  }

  const publicKeyArray = new Uint8Array(publicKeyBuffer);

  // Extract X and Y coordinates from the public key
  // Format varies: could be raw 64 bytes (32+32) or X.509 SPKI format
  let publicKeyX = "";
  let publicKeyY = "";

  if (publicKeyArray.length > 64) {
    // X.509 SPKI format - take last 64 bytes
    const xBytes = publicKeyArray.slice(-64, -32);
    const yBytes = publicKeyArray.slice(-32);
    publicKeyX = Array.from(xBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    publicKeyY = Array.from(yBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } else if (publicKeyArray.length === 64) {
    // Raw coordinates: first 32 = X, last 32 = Y
    const xBytes = publicKeyArray.slice(0, 32);
    const yBytes = publicKeyArray.slice(32, 64);
    publicKeyX = Array.from(xBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    publicKeyY = Array.from(yBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } else {
    throw new Error(`Unexpected public key length: ${publicKeyArray.length}`);
  }

  if (!publicKeyX || !publicKeyY) {
    throw new Error("Failed to extract public key coordinates");
  }

  return {
    credentialId: credential.id,
    publicKeyX: "0x" + publicKeyX,
    publicKeyY: "0x" + publicKeyY,
  };
}

/**
 * Authenticate with an existing passkey (navigator.credentials.get)
 * Returns the assertion data to send to the server
 */
export async function authenticateWithPasskey(): Promise<PasskeyLoginData> {
  if (!isWebAuthnSupported()) {
    throw new Error("WebAuthn is not supported in this browser");
  }

  const rpId = getRpId();

  // Generate browser-side challenge
  const challengeBytes = new Uint8Array(32);
  crypto.getRandomValues(challengeBytes);

  // Get the credential
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: challengeBytes as any,
      rpId,
      userVerification: "required" as const,
      timeout: 120000,
      // NO allowCredentials - enables discoverable credential flow
    },
    mediation: "optional" as CredentialMediationRequirement,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Passkey authentication was cancelled");
  }

  const assertionResponse = credential.response as AuthenticatorAssertionResponse;

  return {
    credentialId: credential.id,
    authenticatorData: bufferToBase64URL(assertionResponse.authenticatorData),
    clientDataJSON: bufferToBase64URL(assertionResponse.clientDataJSON),
    signature: bufferToBase64URL(assertionResponse.signature),
    challenge: bufferToBase64URL(challengeBytes.buffer),
  };
}
